import React from "react";
import { Check, Sparkles, Users, Building2, Zap, Infinity as InfinityIcon } from "lucide-react";
import { Reveal } from "./motion";

// Mesmo destino do botão "Cadastre-se" do cabeçalho (Navbar.jsx)
const AUTH_URL = "https://app.muft.space/auth/login";

/**
 * Planos estáticos (hardcoded) — tabela comercial da landing.
 * Sem integração com API: valores definidos diretamente neste componente.
 */
const plans = [
  {
    id: "essencial",
    name: "Essencial",
    badgeColor: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
    dotColor: "bg-emerald-500",
    branches: "1 filial",
    branchesFeature: "1 Filial inclusa",
    price: "230",
    unitPrice: "R$ 230 por filial",
    highlight: false,
    description: "Ideal para empresas com 1 filial",
  },
  {
    id: "crescimento",
    name: "Crescimento",
    badgeColor: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
    dotColor: "bg-blue-500",
    branches: "Até 3 filiais",
    branchesFeature: "Até 3 Filiais inclusas",
    price: "660",
    unitPrice: "R$ 220 por filial",
    highlight: false,
    description: "Para redes em expansão de até 3 filiais",
  },
  {
    id: "profissional",
    name: "Profissional",
    badgeColor: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20",
    dotColor: "bg-purple-500",
    branches: "Até 5 filiais",
    branchesFeature: "Até 5 Filiais inclusas",
    price: "1.075",
    unitPrice: "R$ 215 por filial",
    highlight: true,
    description: "Até 5 filiais — o mais escolhido",
  },
  {
    id: "empresarial",
    name: "Empresarial",
    badgeColor: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
    dotColor: "bg-amber-500",
    branches: "Até 10 filiais",
    branchesFeature: "Até 10 Filiais inclusas",
    price: "2.100",
    unitPrice: "R$ 210 por filial",
    highlight: false,
    description: "Para redes consolidadas de até 10 filiais",
  },
  {
    id: "expansao",
    name: "Expansão",
    badgeColor: "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20",
    dotColor: "bg-rose-500",
    branches: "Até 20 filiais",
    branchesFeature: "Até 20 Filiais inclusas",
    price: "4.100",
    unitPrice: "R$ 205 por filial",
    highlight: false,
    description: "Para grandes redes de até 20 filiais",
  },
  {
    id: "grande_empresa",
    name: "Grande Empresa",
    badgeColor: "bg-slate-500/10 text-slate-700 dark:text-slate-300 border-slate-500/20",
    dotColor: "bg-slate-700 dark:bg-slate-300",
    branches: "Até 30 filiais",
    branchesFeature: "Até 30 Filiais inclusas",
    price: "6.000",
    unitPrice: "R$ 200 por filial",
    highlight: false,
    description: "Para operações de alta escala até 30 filiais",
  },
];

/** Lista de recursos inclusos por plano (ordem e texto fixos). */
const planFeatures = (plan) => [
  plan.branchesFeature,
  "ERP + PDV + CRM + Ponto Digital + Contabilidade",
  "Contabilidade Expert com Talos IA",
  "Usuários ilimitados",
  "Funcionários e vendedores ilimitados",
  "Produtos e clientes ilimitados",
  plan.id === "essencial" ? "Gestão de estoque e caixa" : "Gestão de estoque e caixa multi-filial",
  plan.id === "essencial" ? "Dashboard e relatórios completos" : "Dashboard e relatórios consolidados",
  "Aplicativo mobile e web",
  "Ranking e pódio de vendedores",
  "Recursos de gestão comercial",
];

const includedFeatures = [
  "ERP + PDV + CRM + Ponto Digital + Contabilidade",
  "Contabilidade Expert com Talos IA",
  "Usuários ilimitados",
  "Funcionários e vendedores ilimitados",
  "Produtos e clientes ilimitados",
  "Gestão de estoque e caixa multi-filial",
  "Dashboard e relatórios consolidados",
  "Aplicativo mobile e web",
  "Ranking e pódio de vendedores",
  "Recursos de gestão comercial",
];

export const Pricing = () => {
  return (
    <section
      id="planos"
      className="relative bg-ice py-24 transition-colors duration-500 dark:bg-navy-950 md:py-32"
      data-testid="pricing-section"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Section Header */}
        <Reveal className="mx-auto mb-16 max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-navy-100 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-navy-800 shadow-sm dark:border-white/10 dark:bg-navy-800 dark:text-lavender">
            <Sparkles className="h-3.5 w-3.5" /> Planos e Preços Transparentes
          </div>
          <h2 className="text-4xl font-black tracking-tight text-navy-800 dark:text-white sm:text-5xl">
            ERP + PDV + CRM + Ponto Digital + Contabilidade em um só lugar.
          </h2>
          <p className="mt-5 text-lg font-semibold text-navy-400 dark:text-navy-100/70">
            Assinatura mensal sem fidelidade. Cadastre toda a sua equipe com <strong className="text-navy-800 dark:text-white font-bold">usuários ilimitados</strong> sem pagar nada a mais por pessoa.
          </p>

          {/* Value Highlights Pill */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-xs font-bold text-navy-800 dark:text-navy-100">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 shadow-sm border border-navy-100 dark:border-white/10 dark:bg-navy-800">
              <Users className="h-4 w-4 text-navy-800 dark:text-lavender" /> Usuários Ilimitados
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 shadow-sm border border-navy-100 dark:border-white/10 dark:bg-navy-800">
              <Building2 className="h-4 w-4 text-navy-800 dark:text-lavender" /> Escale de 1 a 30 Filiais
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 shadow-sm border border-navy-100 dark:border-white/10 dark:bg-navy-800">
              <Zap className="h-4 w-4 text-sky" /> Ativação Imediata
            </span>
          </div>
        </Reveal>

        {/* 6 Plans Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8 items-stretch">
          {plans.map((p, i) => {
            const features = planFeatures(p);
            return (
              <Reveal
                key={p.id}
                delay={i * 0.05}
                className={
                  "relative flex flex-col justify-between rounded-3xl p-8 transition-all duration-300 " +
                  (p.highlight
                    ? "bg-navy-800 text-white shadow-2xl ring-2 ring-lavender/40 lg:-translate-y-2.5"
                    : "border border-navy-100 bg-white hover:border-navy-200 hover:shadow-xl dark:border-white/10 dark:bg-navy-900/70")
                }
                data-testid={"pricing-card-" + p.id}
              >
                {/* Badge: plano mais recomendado */}
                {p.highlight && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span
                      className="inline-flex items-center gap-1.5 rounded-full bg-lavender px-4 py-1 text-[11px] font-black uppercase tracking-widest text-navy-900 shadow-lg"
                      data-testid={"pricing-badge-recommended-" + p.id}
                    >
                      <Sparkles className="h-3 w-3" /> Mais Recomendado
                    </span>
                  </div>
                )}

                {/* Plan Name + Branches */}
                <div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <span className={`h-2.5 w-2.5 rounded-full ${p.dotColor}`} />
                      <h3 className={"text-xl font-black tracking-tight " + (p.highlight ? "text-white" : "text-navy-800 dark:text-white")}>
                        {p.name}
                      </h3>
                    </div>
                    <span
                      className={
                        "rounded-full border px-3 py-1 text-xs font-bold " +
                        (p.highlight ? "border-white/20 bg-white/10 text-white" : p.badgeColor)
                      }
                    >
                      {p.branches}
                    </span>
                  </div>

                  <p className={"mt-2 text-xs font-medium " + (p.highlight ? "text-white/70" : "text-navy-400 dark:text-navy-100/60")}>
                    {p.description}
                  </p>

                  {/* Destaque visual: usuários ilimitados */}
                  <span
                    className={
                      "mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold " +
                      (p.highlight
                        ? "border-lavender/40 bg-lavender/15 text-lavender"
                        : "border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400")
                    }
                    data-testid={"pricing-unlimited-users-" + p.id}
                  >
                    <InfinityIcon className="h-3.5 w-3.5" /> Usuários Ilimitados — sem cobrança por pessoa
                  </span>

                  {/* Price Display */}
                  <div className="mt-6 border-y border-dashed py-5 border-navy-100 dark:border-white/10">
                    <div className="flex items-baseline gap-1">
                      <span className="text-sm font-bold opacity-60">R$</span>
                      <span className={"text-4xl font-black tracking-tight " + (p.highlight ? "text-white" : "text-navy-800 dark:text-white")}>
                        {p.price}
                      </span>
                      <span className="text-sm font-bold opacity-70">/mês</span>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between text-xs font-semibold">
                      <span className={p.highlight ? "text-lavender font-bold" : "text-navy-600 dark:text-lavender"}>
                        {p.unitPrice}
                      </span>
                      <span className="opacity-60">Cobrança mensal</span>
                    </div>
                  </div>

                  {/* Core Features List */}
                  <div className="mt-6 space-y-2.5">
                    <p className="text-xs font-bold uppercase tracking-wider opacity-70">
                      Recursos inclusos no plano:
                    </p>
                    <ul className="space-y-2">
                      {features.map((feat, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs font-medium opacity-90">
                          <span className={"mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full " + (p.highlight ? "bg-lavender text-navy-900" : "bg-navy-50 text-navy-800 dark:bg-white/10 dark:text-lavender")}>
                            <Check className="h-3 w-3" />
                          </span>
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Action Button — mesmo destino do "Cadastre-se" do cabeçalho */}
                <div className="mt-8">
                  <a
                    href={AUTH_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={"pricing-cta-" + p.id}
                    className={
                      "flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold shadow-md transition-all duration-300 hover:scale-[1.02] " +
                      (p.highlight
                        ? "bg-white text-navy-900 hover:bg-lavender"
                        : "bg-navy-800 text-white hover:bg-navy-700 dark:bg-white dark:text-navy-900")
                    }
                  >
                    Cadastre-se no {p.name}
                  </a>
                  <p className="mt-2 text-center text-[11px] font-medium opacity-60">
                    Sem fidelidade · Cancele quando quiser
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Global Features Banner */}
        <Reveal delay={0.3} className="mt-16 rounded-3xl border border-navy-100 bg-white p-8 shadow-sm dark:border-white/10 dark:bg-navy-900">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <h4 className="text-2xl font-black text-navy-800 dark:text-white">
              Tudo incluso em todos os planos do MUFT
            </h4>
            <p className="mt-2 text-sm text-navy-400 dark:text-navy-100/60 font-medium">
              Sem taxas extras por módulo ou cobranças surpresa. Uma única assinatura para operar sua empresa inteira.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {includedFeatures.map((feat, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 rounded-xl border border-navy-50 bg-ice/60 p-3.5 dark:border-white/5 dark:bg-navy-950/50"
              >
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-navy-800 text-white dark:bg-lavender dark:text-navy-900">
                  <Check className="h-3.5 w-3.5" />
                </div>
                <span className="text-xs font-bold text-navy-800 dark:text-navy-100">
                  {feat}
                </span>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Pricing;